import { useEffect, useState } from "react";
import { useAuth } from "../../../../Providers/Auth";
import {
  ScrollArea,
  ScrollBar,
} from "../../../../shadcnComponents/ui/scroll-area";
import {
  CircleCheck,
  Eye,
  Image,
  Info,
  Laptop,
  Loader,
  Repeat2,
  Send,
  Smartphone,
  Video,
} from "lucide-react";
import { cn } from "../../../../lib/utils";
import { Button } from "../../../../shadcnComponents/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../shadcnComponents/ui/tooltip";
import { handlePreview } from "@/Pages/Brand/DashBoard/PromoteAd/Helpers/Helper";

// types ----------------------
interface InitialEditorProps {
  content?: string;
  dummyText?: string;
  influencerName?: string;
  tagline?: string;
  profileImage?: string;
  images?: File[];
  video?: File[] | string[];
  viewMode?: "desktop" | "mobile";
  docid?: string;
  editorMode?: "edit" | "view";
  status?: 0 | 1 | 2 | 3;
  suggestionComment?: string;
  suggestionImages?: string;
  isApproved?: boolean;
}

// components ----------------------

interface HeaderProps {
  title: string;
  description?: string;
}

const Header = ({ title, description }: HeaderProps) => {
  return (
    <div className="text-center space-y-2">
      <h1 className="text-[#FFF] font-[Inter] text-[20px] not-italic font-bold leading-[24px]">
        {title}
      </h1>
      {description && (
        <p className="text-[#9E9E9E] text-center font-[Inter] text-[14px] not-italic font-normal leading-[20px]">
          {description}
        </p>
      )}
    </div>
  );
};

interface ToolbarProps {
  setViewMode: (mode: string) => void;
  viewMode: string;
}

const Toolbar = ({ setViewMode, viewMode }: ToolbarProps) => {
  return (
    <div className="relative w-full flex items-center gap-5 justify-between">
      <div className="text-xs ">
        <p className="text-red-400">
          Note: Declare #partnership #anchors unless the brand requests removal
        </p>
      </div>
      <div className="flex gap-2 items-center justify-end flex-grow">
        <span className="text-[#FFF] font-[Inter] text-[14px] not-italic font-semibold leading-[24px]">
          Preview
        </span>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={19} color="#666666" />
            </TooltipTrigger>
            <TooltipContent className="bg-white border ">
              <p className="w-[300px] text-xs text-center">
                Toggle between mobile and desktop views to see how your content
                would appear on LinkedIn
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="px-2 py-1 bg-[#424242] rounded-md flex items-center justify-center">
        {[
          {
            icon: <Smartphone size={20} />,
            mode: "mobile",
          },
          {
            icon: <Laptop size={20} />,
            mode: "desktop",
          },
        ]?.map((item, i) => {
          return (
            <button
              key={i}
              className={cn(
                "p-2 rounded-md hover:text-[#b4b3b3]",
                viewMode === item?.mode && "bg-[#5C5B5B]"
              )}
              onClick={() => setViewMode(item?.mode)}
            >
              {item?.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

interface InputTextAreaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {}

const InputTextArea = ({ ...props }: InputTextAreaProps) => {
  return (
    <textarea
      className="w-full p-5 bg-transparent min-h-[175px] h-full outline-none text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-normal leading-[22px]"
      {...props}
    />
  );
};

interface AddImageAreaProps {
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  handleVideoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleVideoRemove: () => void;
  onSaveDraft: ({
    e,
    editorState,
  }: {
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>;
    editorState?: InitialEditorProps;
  }) => void;
  onSendForApproval: ({
    e,
    editorState,
  }: {
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>;
    editorState?: InitialEditorProps;
  }) => void;
  editorState: InitialEditorProps;
  isDraftSaving?: boolean;
  isDraftExpired?: boolean;
}

const AddImageArea = ({
  handleFileChange,
  removeImage,
  onSaveDraft,
  onSendForApproval,
  editorState,
  isDraftSaving,
  isDraftExpired,
  handleVideoChange,
  handleVideoRemove,
}: AddImageAreaProps) => {
  return (
    <div className="w-full p-5">
      <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[22px]">
        {editorState?.editorMode === "edit" && "Add Images/Videos"}
        {editorState?.editorMode === "view" &&
          (editorState?.images?.length > 0 || editorState?.video?.length > 0) &&
          "Image/Video Added"}
      </div>

      <ScrollArea className="py-2">
        <div className="flex gap-2">
          {editorState?.editorMode === "edit" && (
            <div className="flex gap-2">
              <div className="flex items-center justify-center my-2 mr-2 size-20  shrink-0">
                <input
                  disabled={isDraftExpired}
                  id="file-upload"
                  className="hidden"
                  type="file"
                  multiple
                  accept=".png, .jpg"
                  onChange={(e) => handleFileChange(e)}
                />
                <label
                  htmlFor="file-upload"
                  className="flex justify-center items-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer flex-col gap-1 py-2 px-1 text-center shrink-0 size-full"
                >
                  <Image size={20} color="#666666" className="shrink-0" />
                  <span className="text-[#E2E8F0] font-[Inter] text-[10px] not-italic font-normal leading-[22px]">
                    Select Image
                  </span>
                </label>
              </div>
              <div className="flex items-center justify-center my-2 mr-2 size-20  shrink-0">
                <input
                  disabled={isDraftExpired}
                  id="video-upload"
                  className="hidden"
                  type="file"
                  accept="video/*"
                  onChange={(e) => handleVideoChange(e)}
                />
                <label
                  htmlFor="video-upload"
                  className="flex justify-center items-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer flex-col gap-1 py-2 px-1 text-center shrink-0 size-full"
                >
                  <Video size={20} color="#666666" className="shrink-0" />
                  <span className="text-[#E2E8F0] font-[Inter] text-[10px] not-italic font-normal leading-[22px]">
                    Select Video
                  </span>
                </label>
              </div>
            </div>
          )}

          {editorState?.images?.map((file, index) => (
            <div
              key={index}
              className="relative m-2 shrink-0 flex flex-col gap-2 "
            >
              <div key={index} className="relative group">
                <img
                  className="w-20 h-20 object-cover rounded-md"
                  src={file instanceof Blob ? URL.createObjectURL(file) : file}
                  alt={`preview-${index}`}
                />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      handlePreview(file);
                    }}
                    variant="outline"
                    className="text-white-700 bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                  >
                    <Eye size={10} />
                    Preview
                  </Button>
                </div>
              </div>
              {!(file instanceof Blob) && (
                <span className="text-xs">
                  {(file as string)?.split("-")?.pop()}
                </span>
              )}
              {editorState?.editorMode === "edit" && (
                <div
                  onClick={() => removeImage(index)}
                  className="w-4 h-4 flex items-center justify-center text-sm absolute top-1 right-1 bg-red-500 text-white rounded-full cursor-pointer"
                >
                  &times;
                </div>
              )}
              {/* Preview Button */}
            </div>
          ))}
        </div>
        {editorState?.video?.length > 0 &&
          editorState?.video?.map((file, index) => (
            <div
              key={index}
              className="relative m-2 shrink-0 flex flex-col gap-2 "
            >
              <div className="w-full h-full flex justify-center items-center">
                <video
                  className="rounded-md shadow-md w-3/4"
                  controls
                  autoPlay={false}
                  src={file instanceof Blob ? URL.createObjectURL(file) : file}
                />
              </div>

              {!(file instanceof Blob) && (
                <span className="text-xs">
                  {(file as string)?.split("-")?.pop()}
                </span>
              )}
              {editorState?.editorMode === "edit" && (
                <div
                  onClick={() => handleVideoRemove()}
                  className="w-4 h-4 flex items-center justify-center text-sm absolute top-1 right-1 bg-red-500 text-white rounded-full cursor-pointer"
                >
                  &times;
                </div>
              )}
            </div>
          ))}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>

      <div className="flex gap-3 py-2">
        {editorState?.editorMode === "edit" && (
          <Button
            disabled={isDraftExpired}
            variant="outline"
            className="bg-transparent min-w-24"
            onClick={(e) => {
              onSaveDraft({ e, editorState });
            }}
          >
            {isDraftSaving ? <Loader className="animate-spin" /> : "Save Draft"}
          </Button>
        )}
        <Button
          variant="destructive"
          onClick={(e) => onSendForApproval({ e, editorState })}
          disabled={!(editorState?.editorMode === "edit") || isDraftExpired}
        >
          {editorState?.editorMode === "edit"
            ? "Send for approval"
            : "Submitted for approval"}
        </Button>
      </div>
    </div>
  );
};

interface PreviewProps {
  content: string;
  viewMode: "mobile" | "desktop";
  dummyText?: string;
  currentUserName?: string;
  profileImage?: string;
  tagline?: string;
  suggestedMode?: boolean;
}

const Preview = ({
  content,
  viewMode,
  dummyText = "",
  currentUserName,
  profileImage,
  tagline,
  suggestedMode,
}: PreviewProps) => {
  const [firstPart, setFirstPart] = useState("");
  const [secondPart, setSecondPart] = useState("");
  const [seeMore, setSeeMore] = useState(false);

  useEffect(() => {
    const splitContent = (text) => {
      const lines = text.split("\n");
      const firstThreeLines = lines.slice(0, 3).join("\n");
      const remainingLines = lines.slice(3).join("\n");
      return [firstThreeLines, remainingLines];
    };
    if (content?.trim()?.length > 0) {
      content = content.trim();
      const [first, second] = splitContent(content);
      setFirstPart(first);
      setSecondPart(second);
    } else {
      setFirstPart(dummyText);
      setSecondPart("");
    }
  }, [content]);

  return (
    <div
      className={cn(
        "w-full rounded-lg bg-[#1B1F23] flex flex-col p-6 gap-2",
        `${
          viewMode === "mobile"
            ? "w-[390px]"
            : viewMode === "desktop"
            ? "w-[555px]"
            : ""
        }`
      )}
    >
      <div className="flex items-center gap-3">
        {!profileImage ? (
          <div className="size-[50px] rounded-full bg-[#262626] shrink-0"></div>
        ) : (
          <img
            src={profileImage}
            alt="image"
            className="size-[50px] shrink-0 object-cover rounded-full"
          />
        )}
        <div className="w-full">
          <h1 className="text-[#FFF] font-[Inter] text-[14px] not-italic font-semibold leading-[24px] line-clamp-1">
            {currentUserName || "Your Name"}
          </h1>
          <p className="text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3">
            {tagline || "Your tagline"}
          </p>
          <p className="text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3 mt-1">
            1d
          </p>
        </div>
      </div>
      <div className="pb-3">
        {suggestedMode ? (
          <p>
            <span
              className={cn(
                "text-[#C7C3C3] font-inter text-[12px] not-italic font-normal leading-[22px] relative overflow-hidden",
                seeMore ? "line-clamp-none" : "line-clamp-3"
              )}
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                whiteSpace: "pre-wrap",
              }}
            >
              {content}
            </span>
            <div
              className="text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[22px] hover:underline bg-[#1b1f23] cursor-pointer w-full text-right"
              onClick={() => setSeeMore(!seeMore)}
            >
              {seeMore ? "...see less" : "...see more"}
            </div>
          </p>
        ) : (
          <>
            <p>
              <span
                className="text-[#C7C3C3] font-inter text-[12px] not-italic font-normal leading-[22px] relative overflow-hidden"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  textOverflow: "clip",
                  whiteSpace: "pre-wrap",
                }}
              >
                {firstPart}
                <span className="text-[#3771C8] font-[Inter] text-[12px] not-italic font-normal leading-[22px] hover:underline absolute right-0 bottom-0 bg-[#1b1f23]">
                  ...see more
                </span>
              </span>
            </p>
            <p
              className="text-[#666] font-Inter text-[12px] not-italic font-normal leading-[22px]"
              style={{
                whiteSpace: "pre-wrap",
              }}
            >
              {secondPart}
            </p>
          </>
        )}
      </div>
      <div className="flex gap-2">
        <img
          src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727856335032-Frame_1430102479_%281%29.png"
          alt="reactions"
          className="h-4"
        />
        <span className="text-[#949698] font-[Inter] text-[12px] not-italic font-bold leading-[normal]">
          Harsh Pal and 76 others
        </span>
      </div>
      <div className="flex gap-5 items-center pt-3">
        {!profileImage ? (
          <div className="size-[50px] rounded-full bg-[#262626] shrink-0"></div>
        ) : (
          <img
            src={profileImage}
            alt="image"
            className="size-[50px] shrink-0 object-cover rounded-full"
          />
        )}
        {[
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727853788915-ei_like.png"
                alt="like"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Like",
          },
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727855749998-lets-icons_comment-light.png"
                alt="comment"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Comment",
          },
          {
            icon: <Repeat2 size={24} color="#787b7d" />,
            title: "Repost",
          },
          {
            icon: <Send size={19} color="#787b7d" />,
            title: "Share",
          },
        ]?.map((item, i) => {
          return (
            <div key={i} className="flex gap-1 items-center justify-center">
              {item?.icon}
              {!(viewMode === "mobile") && (
                <span className="text-[#949698] font-inter text-[12px] flex items-center justify-center">
                  {item?.title}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SuggestionArea = ({ editorState }) => {
  const [seeMore, setSeeMore] = useState(false);
  console.log(editorState);
  return (
    <div className="p-5">
      {(editorState?.images?.length > 0 || editorState?.video?.length > 0) && (
        <div className="w-full">
          <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[24px]">
            Image(s) or video(s) to be added
          </div>

          <ScrollArea className="py-2">
            <div className="flex gap-2">
              {editorState?.images?.map((file, index) => (
                <div
                  key={index}
                  className="relative m-2 shrink-0 flex flex-col gap-2"
                >
                  <div key={index} className="relative group">
                    <img
                      className="w-20 h-20 object-cover rounded-md"
                      src={
                        file instanceof Blob ? URL.createObjectURL(file) : file
                      }
                      alt={`preview-${index}`}
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                      <Button
                        onClick={(e) => {
                          e.preventDefault();
                          handlePreview(file);
                        }}
                        variant="outline"
                        className="text-white-700 bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                      >
                        <Eye size={10} />
                        Preview
                      </Button>
                    </div>
                  </div>

                  {!(file instanceof Blob) && (
                    <span className="text-xs">
                      {(file as string)?.split("-")?.pop()}
                    </span>
                  )}
                </div>
              ))}
            </div>
            {editorState?.video?.length > 0 &&
              editorState?.video?.map((file, index) => (
                <div
                  key={index}
                  className="relative m-2 shrink-0 flex flex-col gap-2"
                >
                  <div className="w-full h-full flex justify-center items-center">
                    <video
                      className="rounded-md shadow-md w-3/4 "
                      controls
                      autoPlay={false}
                      src={
                        file instanceof Blob ? URL.createObjectURL(file) : file
                      }
                    />
                  </div>
                  {!(file instanceof Blob) && (
                    <span className="text-xs">
                      {(file as string)?.split("-")?.pop()}
                    </span>
                  )}
                </div>
              ))}
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      )}
      {editorState?.suggestionComment?.length > 0 && (
        <>
          <div className="text-[#E2E8F0] font-[Inter] text-[14px] not-italic font-semibold leading-[24px] pb-2">
            Suggestion
          </div>
          <div className="bg-[#1B1F23] p-3 rounded-lg w-fit">
            <p
              className={cn(
                "text-[#949698] font-[Inter] text-[12px] not-italic font-normal leading-[22px] whitespace-pre-wrap cursor-pointer max-w-sm break-words",
                seeMore ? "line-clamp-none" : "line-clamp-6"
              )}
              onClick={() => setSeeMore(!seeMore)}
            >
              {editorState?.suggestionComment}
              <div className="flex gap-2">
                {editorState?.suggestionImages?.map((file, index) => (
                  <div
                    key={index}
                    className="relative m-2 shrink-0 flex flex-col gap-2"
                  >
                    <div key={index} className="relative group">
                      <img
                        className="w-20 h-20 object-cover rounded-md"
                        src={
                          file instanceof Blob
                            ? URL.createObjectURL(file)
                            : file
                        }
                        alt={`preview-${index}`}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            handlePreview(file);
                          }}
                          variant="outline"
                          className="text-white-700 bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                        >
                          <Eye size={10} />
                          Preview
                        </Button>
                      </div>
                    </div>

                    {!(file instanceof Blob) && (
                      <span className="text-xs">
                        {(file as string)?.split("-")?.pop()}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </p>
          </div>
        </>
      )}
      {editorState?.isApproved && (
        <div
          className={
            "py-1 px-4 font-[Inter] text-[12px] not-italic font-semibold leading-[24px] rounded-[6px] uppercase bg-[#36D7CC33]/20 text-[#fff] flex gap-2 items-center w-fit"
          }
        >
          <CircleCheck fill="#115E59" color="#fff" />
          APPROVED DRAFT FOR CAMPAIGN
        </div>
      )}
    </div>
  );
};

// LinkedinPostEditor ----------------------

interface LinkedinPostEditorProps {
  initial?: InitialEditorProps;
  header?: HeaderProps;
  onSaveDraft?: AddImageAreaProps["onSaveDraft"];
  onSendForApproval?: AddImageAreaProps["onSendForApproval"];
  isDraftSaving?: boolean;
  isDraftExpired?: boolean;
}

export const LinkedinPostEditor = ({
  initial = {},
  header,
  onSaveDraft,
  onSendForApproval,
  isDraftSaving,
  isDraftExpired,
}: LinkedinPostEditorProps) => {
  const authState = useAuth();
  const [editorState, setEditorState] = useState<InitialEditorProps>({
    content: "#partnership #anchors",
    dummyText: `Start writing and your post will appear here..\n\nYou can add images, links, #hashtags and emojis 🤩`,
    influencerName: "",
    tagline: "",
    profileImage: "",
    images: [],
    video: [],
    viewMode: "desktop",
    docid: "",
    editorMode: "edit",
    status: 3,
    suggestionComment: "",
    isApproved: false,
  });

  useEffect(() => {
    setEditorState((prev) => ({
      ...prev,
      influencerName:
        initial.influencerName || authState?.loggedUser?.name || "",
      tagline: initial.tagline || authState?.loggedUser?.tagline || "",
      profileImage:
        initial.profileImage || authState?.loggedUser?.profile || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    setEditorState((prev) => ({
      ...prev,
      ...initial,
    }));
  }, [initial]);

  const handleViewModeChange = (mode: InitialEditorProps["viewMode"]) => {
    setEditorState((prev) => ({
      ...prev,
      viewMode: mode,
    }));
  };

  const handleEditorTextChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setEditorState((prev) => ({ ...prev, content: e.target.value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setEditorState((prev) => ({
        ...prev,
        images: [...Array.from(e.target.files), ...prev.images],
      }));
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setEditorState((prev) => ({
        ...prev,
        video: [file],
      }));
    }
  };

  const handleImageRemove = (index: number) => {
    setEditorState((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleVideoRemove = () => {
    setEditorState((prev) => ({
      ...prev,
      video: [],
    }));
  };

  return (
    <>
      <div className="flex flex-col gap-5">
        {header && (
          <Header title={header?.title} description={header?.description} />
        )}
        <div className="flex flex-col">
          <div className="border border-[#262626] px-7 py-2 rounded-t-lg">
            <Toolbar
              setViewMode={handleViewModeChange}
              viewMode={editorState?.viewMode}
            />
          </div>
          <div className="flex h-full ">
            <div className="w-1/2 flex flex-col border border-[#262626] rounded-l-lg rounded-t-none">
              {editorState?.isApproved || editorState?.suggestionComment ? (
                <SuggestionArea editorState={editorState} />
              ) : (
                <>
                  <div className="flex-1">
                    <InputTextArea
                      placeholder="Write here..."
                      onChange={handleEditorTextChange}
                      value={editorState?.content}
                      style={{ scrollbarWidth: "thin" }}
                      disabled={
                        !(editorState?.editorMode === "edit") || isDraftExpired
                      }
                    />
                  </div>
                  <AddImageArea
                    handleFileChange={handleImageChange}
                    removeImage={handleImageRemove}
                    handleVideoChange={handleVideoChange}
                    handleVideoRemove={handleVideoRemove}
                    onSaveDraft={onSaveDraft}
                    onSendForApproval={onSendForApproval}
                    editorState={editorState}
                    isDraftSaving={isDraftSaving}
                    isDraftExpired={isDraftExpired}
                  />
                </>
              )}
            </div>
            <div className="flex-1 min-w-1/2 max-w-7/12 flex flex-col border-[#262626] rounded-r-lg rounded-tr-none bg-[#262626] items-center">
              <ScrollArea className="p-6">
                <Preview
                  content={editorState?.content}
                  viewMode={editorState?.viewMode}
                  dummyText={editorState?.dummyText}
                  currentUserName={editorState?.influencerName}
                  profileImage={editorState?.profileImage}
                  tagline={editorState?.tagline}
                  suggestedMode={
                    !!editorState?.suggestionComment || editorState?.isApproved
                  }
                />
              </ScrollArea>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
